import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// v10.21.20: app version no longer imported here. The pre-login UI
// must not advertise the build number — that's a free fingerprint
// for anyone scanning for known-CVE bundles. Authenticated surfaces
// can still surface the version (footer, /health, etc.).
import {
  Container, Paper, TextField, Button, Typography, Box,
  Alert, CircularProgress, InputAdornment, IconButton,
  useTheme, alpha,
} from '@mui/material';
import {
  Visibility, VisibilityOff,
  Videocam as VideocamIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { userLogin } from '../store/slices/authSlice';

function ScreeningRoomLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { loading, error, userInfo } = useSelector((state) => state.auth);

  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // v10.19.7: campaigns is the producer-facing rollup of every DCP +
    // watermark screener tied to a film. It's the first thing operators
    // see post-login because most distribution work threads through a
    // campaign now; the dashboard is still one click away from the
    // sidebar (and from the workspace logo) for ops/system checks.
    if (userInfo) navigate('/campaigns');
  }, [navigate, userInfo]);

  // v10.21.20: purge stale tus-js-client resume metadata from
  // localStorage on the unauthenticated login page. tus-js-client
  // persists upload URLs + metadata under keys prefixed with `tus::`
  // so a refresh can resume mid-flight. That state has no business
  // hanging around for the next person to use this browser —
  // forensic value zero, fingerprint value non-zero (filename, size,
  // company id all readable from the DOM without auth). Cleared
  // here defensively in case the post-upload cleanup or logout
  // sweep missed any keys.
  useEffect(() => {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('tus::')) keys.push(k);
      }
      keys.forEach((k) => localStorage.removeItem(k));
    } catch (_) {
      // localStorage unavailable (private mode etc.); nothing to clear.
    }
  }, []);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(userLogin(credentials));
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isDark ? '#050608' : '#f0efe8',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Cinematic ambient glow */}
      {isDark && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: '-20%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120%',
              height: '50%',
              background: 'radial-gradient(ellipse, rgba(212, 160, 23, 0.06) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80%',
              height: '30%',
              background: 'radial-gradient(ellipse, rgba(212, 160, 23, 0.03) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            bgcolor: isDark
              ? alpha('#12151c', 0.85)
              : theme.palette.background.paper,
            backdropFilter: 'blur(24px)',
            border: `1px solid ${isDark ? 'rgba(212, 160, 23, 0.1)' : 'rgba(0,0,0,0.06)'}`,
            boxShadow: isDark
              ? '0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(212, 160, 23, 0.05)'
              : '0 8px 32px rgba(0,0,0,0.08)',
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <VideocamIcon
              sx={{
                fontSize: 44,
                mb: 1.5,
                color: theme.palette.primary.main,
                filter: isDark ? 'drop-shadow(0 0 12px rgba(212, 160, 23, 0.3))' : 'none',
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                textAlign: 'center',
              }}
            >
              Screening Room
            </Typography>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.primary.main,
                mt: 0.5,
              }}
            >
              Cinema Distribution
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* v10.21.20: method="post" defends against credentials
              leaking into URL bar / referer / proxy logs if the React
              onSubmit handler ever fails to intercept (network error
              during bundle load, JS disabled, etc.). action="" keeps
              the browser-fallback submission same-origin so it can't
              be hijacked to a third-party endpoint.
              autoComplete="on" at the form level pairs with the per-
              field autocomplete attributes so password managers
              consistently autofill.

              Note for security reviewers landing on this form:
                * CSRF token: not needed. Authentication is bearer-
                  token (JWT in response body, stored in localStorage
                  by the SPA). The cross-origin same-origin policy
                  on fetch responses means a malicious page can't
                  read the JWT even if it could force a login. See
                  backend api/auth.py:login docstring.
                * Rate limit / CAPTCHA: enforced server-side at 20
                  requests per minute per real client IP (slowapi,
                  see backend utils/rate_limit.py + client_ip.py).
                  No client-side widget by design — reduces
                  fingerprint surface and isn't bypassed by a
                  scripted attacker the way a CAPTCHA can be. */}
          <Box component="form" method="post" action="" autoComplete="on" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              inputProps={{ maxLength: 256 }}
              value={credentials.username}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #12151c inset',
                  WebkitTextFillColor: '#e8e6e3',
                  caretColor: '#e8e6e3',
                  borderRadius: 'inherit',
                  transition: 'background-color 9999s ease-in-out 0s',
                },
              }}
            />
            <TextField
              margin="normal"
              required fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              inputProps={{ maxLength: 256 }}
              value={credentials.password}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end" disabled={loading} size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #12151c inset',
                  WebkitTextFillColor: '#e8e6e3',
                  caretColor: '#e8e6e3',
                  borderRadius: 'inherit',
                  transition: 'background-color 9999s ease-in-out 0s',
                },
              }}
            />

            <Button
              type="submit"
              fullWidth variant="contained"
              disabled={loading || !credentials.username || !credentials.password}
              sx={{
                mt: 3, mb: 2, py: 1.5,
                fontSize: '0.95rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                background: isDark
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                color: isDark ? '#0a0c10' : '#fff',
                '&:hover': {
                  boxShadow: isDark
                    ? '0 4px 20px rgba(212, 160, 23, 0.3)'
                    : '0 4px 16px rgba(0,0,0,0.15)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>

          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default ScreeningRoomLogin;
